import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
    { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'ru', name: 'Russian', flag: '🇷🇺' },
    { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
];

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
    const { i18n } = useTranslation();
    const [language, setLanguage] = useState(() => {
        const savedLang = localStorage.getItem('language') || 'en';
        return savedLang;
    });

    // Initialize language on mount
    useEffect(() => {
        const savedLang = localStorage.getItem('language') || 'en';
        if (i18n.language !== savedLang) {
            i18n.changeLanguage(savedLang);
        }
        setLanguage(savedLang);
    }, []);

    // Set RTL on mount and language change
    useEffect(() => {
        const currentLang = i18n.language || language || 'en';
        if (currentLang === 'ar') {
            document.documentElement.setAttribute('dir', 'rtl');
            document.documentElement.setAttribute('lang', 'ar');
        } else {
            document.documentElement.setAttribute('dir', 'ltr');
            document.documentElement.setAttribute('lang', currentLang);
        }
    }, [i18n.language, language]);

    // Listen for language changes from i18n
    useEffect(() => {
        const handleLanguageChanged = (lng) => {
            setLanguage(lng);
        };
        i18n.on('languageChanged', handleLanguageChanged);
        return () => {
            i18n.off('languageChanged', handleLanguageChanged);
        };
    }, [i18n]);

    const changeLanguage = (langCode) => {
        i18n.changeLanguage(langCode);
        localStorage.setItem('language', langCode);
        setLanguage(langCode);
        
        // Set RTL for Arabic
        if (langCode === 'ar') {
            document.documentElement.setAttribute('dir', 'rtl');
            document.documentElement.setAttribute('lang', 'ar');
        } else {
            document.documentElement.setAttribute('dir', 'ltr');
            document.documentElement.setAttribute('lang', langCode);
        }
    };

    const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

    return (
        <LanguageContext.Provider value={{ 
            language, 
            changeLanguage, 
            languages, 
            currentLanguage,
            i18n 
        }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
}
